import React, {useEffect, useState} from "react";

import {useQueryClient} from "@tanstack/react-query";

import useCutoffTimes, {CutoffTimes} from "@globals/hooks/useCutoffTimes";
import {MENU_DATES_QUERY} from "@globals/hooks/useMenuDates";

import {
  ConfirmationModal,
  ConfirmationModalProps,
  Modal,
} from "@components/modals";

type CutoffModalProps = Omit<ConfirmationModalProps, "isOpen" | "title"> & {
  title?: string;
  cutoffTimes?: CutoffTimes;
  isLargeOrder?: boolean;
  showOnLoad?: boolean;
  isExtras?: boolean;
};

export default function CutoffModal({
  cutoffTimes: initialCutoffTimes,
  isLargeOrder = false,
  showOnLoad = false,
  isExtras = false,
  onClose,
  ...otherProps
}: CutoffModalProps): JSX.Element {
  const {
    isFetched,
    isPastCutoffTime,
    isPastLargeOrderCutoffTime,
    hasPastCutoffTimer,
    hasPastLargeOrderCutoffTimer,
    cutoffTimes,
  } = useCutoffTimes({
    initialCutoffTimes,
  });
  const [isCutoffModalVisible, setIsCutoffModalVisible] =
    useState<boolean>(false);
  const queryClient = useQueryClient();

  const showCutoffModal = () => setIsCutoffModalVisible(true);
  const hideCutoffModal = () => setIsCutoffModalVisible(false);
  const onCloseCallback = () => {
    onClose?.();
    hideCutoffModal();
    // Refresh menu dates after cutoff modal close;
    queryClient.invalidateQueries({queryKey: [MENU_DATES_QUERY]});
  };

  // check hasPastCutoffTimer to ensure that last fetched remaining is not zero which means that isPastCutoffTime changed on timer event
  const isPastForOrder = isPastCutoffTime && (hasPastCutoffTimer || showOnLoad);
  const isPastForLargeOrder =
    isLargeOrder &&
    isPastLargeOrderCutoffTime &&
    (hasPastLargeOrderCutoffTimer || showOnLoad);

  useEffect(() => {
    if (isFetched) {
      if (isPastForOrder || isPastForLargeOrder) {
        showCutoffModal();
      } else {
        hideCutoffModal();
      }
    }
  }, [isFetched, isPastForOrder, isPastForLargeOrder]);

  if (!isFetched) {
    return <></>;
  }

  let title = "Sorry, time's up!";
  let confirmationContent: string | JSX.Element = (
    <>
      We stopped taking orders for this menu date at{" "}
      <b>{cutoffTimes?.order_cutoff}</b>.
    </>
  );
  if (isExtras) {
    title = `It's past the ${cutoffTimes?.order_cutoff} cutoff!`;
    confirmationContent = "Orders cannot be changed anymore.";
  }

  return (
    <Modal
      isOpen={isCutoffModalVisible}
      modalComponent={ConfirmationModal}
      title={title}
      confirmationContent={confirmationContent}
      confirmButtonText="OK"
      cancelButtonText=""
      onClose={onCloseCallback}
      {...otherProps}
    />
  );
}
